import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Features from "../../components/landingPage/features";
import PeakScore from "../../components/landingPage/peakScore";
import FloatingHeader from "../../components/landingPage/floatingMenu";
import Network from "../../components/landingPage/network";
import Intercom from "@intercom/messenger-js-sdk";
import { safeGetItem, safeSetItem } from "../../utils/localStorageUtils";
import Users from "../../components/landingPage/users";
import Logos from "../../components/landingPage/logos";
import {
  ChevronDownIcon,
  CheckIcon,
  StarIcon,
} from "@heroicons/react/20/solid";

// ─── Data ────────────────────────────────────────────────────────────────────

const v2Features = [
  {
    title: "Personalise Insights",
    description:
      "Leverage advanced algorithms & AI to gain personalised insights tailored to your unique data profile.",
  },
  {
    title: "Training Load Analysis",
    description:
      "What training you are doing measured by RPE x Time. Understand your acute and chronic load balance.",
  },
  {
    title: "Recovery Monitoring",
    description:
      "How you are responding to training measured with subjective check-ins and physiological data.",
  },
  {
    title: "Physiological Adaption Analysis",
    description:
      "Track how your cardiorespiratory system adapts over time using wearable data like HRV, resting heart rate, and respiratory rate.",
  },
  {
    title: "Centralise Data in One Place",
    description:
      "All your training, wellness, and wearable data in one place — owned by you. Export, analyse, or delete anytime.",
  },
];

const testimonials = [
  {
    name: "Sarah M.",
    role: "Triathlete",
    quote:
      "Peak has transformed how I approach training. The readiness score gives me confidence to push hard on the right days and recover when I need to.",
    rating: 5,
  },
  {
    name: "James K.",
    role: "CrossFit Athlete",
    quote:
      "The combination of subjective wellness and wearable data gives me the full picture that wearables alone can't provide. Game changer.",
    rating: 5,
  },
  {
    name: "Coach David L.",
    role: "Athletics Coach",
    quote:
      "Managing 20+ athletes used to be guesswork. Now I can see exactly who needs attention and who's ready to perform at their best.",
    rating: 5,
  },
];

const faqItems = [
  {
    question: "How does the Peak Score work?",
    answer:
      "The Peak Score is a daily readiness signal based on three key inputs: your subjective wellness, training load balance, and physiological data from wearables. It reflects how prepared you are to tolerate training stress and express performance on a given day.",
  },
  {
    question: "Do I need a wearable device?",
    answer:
      "No. Peak works with just your daily training and wellness logs. However, connecting a wearable device (Apple Watch, Garmin, Whoop, Oura, etc.) enhances your insights by adding heart rate, sleep, HRV, and activity data.",
  },
  {
    question: "How long until I see meaningful insights?",
    answer:
      "Your Peak Score starts working after 7 days of consistent logging. The most accurate scoring occurs after 28 days, as the system builds a stronger baseline of your personal data.",
  },
  {
    question: "Can I use Peak as a coach?",
    answer:
      "Yes. The Athlete Monitoring Portal (AMS) at ams.peak.app is built for coaches, teams and organisations. Connect with your athletes to view their readiness, training load, and wellness data in one dashboard.",
  },
  {
    question: "Is my data secure?",
    answer:
      "Absolutely. Peak is built with privacy-first architecture and industry-leading security protocols. Your data belongs to you — analyse it, export it, or delete it anytime.",
  },
];

const logTypes = [
  {
    name: "Training Load",
    description:
      "Log RPE, duration, and training tags to measure your load over time.",
    icon: "dumbbell",
    chip: "#EBEBEB",
    stroke: "#3D4451",
  },
  {
    name: "Wellness",
    description:
      "Daily subjective check-in covering mood, sleep quality, stress, recovery, and physical state.",
    icon: "heart",
    chip: "#E7F6EC",
    stroke: "#1F7A3D",
  },
  {
    name: "Recovery",
    description:
      "Track recovery modalities — ice baths, massage, stretching, compression, and more.",
    icon: "leaf",
    chip: "#E9F0FE",
    stroke: "#2563EB",
  },
  {
    name: "Injuries & Niggles",
    description:
      "Monitor pain levels, impact on training, and injury progression over time.",
    icon: "alert",
    chip: "#FFF1DC",
    stroke: "#EA580C",
  },
  {
    name: "Performance",
    description:
      "Record performance results, expectations, personal bests, and season's bests.",
    icon: "trophy",
    chip: "#FFF8E1",
    stroke: "#8A6D00",
  },
  {
    name: "Sleep & Health",
    description:
      "Track HRV, resting heart rate, respiratory rate, and sleep duration from wearables.",
    icon: "health",
    chip: "#F1ECFB",
    stroke: "#7C3AED",
  },
  {
    name: "Activities",
    description:
      "Auto-sync GPS activities, heart rate zones, and calorie data from 11+ wearable brands.",
    icon: "activity",
    chip: "#EBEBEB",
    stroke: "#3D4451",
  },
  {
    name: "Female Cycle",
    description:
      "Track menstrual cycles alongside training to understand how your cycle affects performance.",
    icon: "cycle",
    chip: "#FCE7F1",
    stroke: "#BE185D",
  },
  {
    name: "Notes",
    description:
      "Capture daily reflections, coach feedback, or anything relevant to your training journal.",
    icon: "file",
    chip: "#EBEBEB",
    stroke: "#3D4451",
  },
];

// ─── FAQ Accordion Item ──────────────────────────────────────────────────────

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-base-300">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-5 text-left font-grotesk font-medium text-base-content hover:text-base-content/80 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        aria-expanded={isOpen}
      >
        <span className="pr-4">{question}</span>
        <ChevronDownIcon
          className={`w-5 h-5 shrink-0 text-base-content/40 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? "max-h-96 pb-5" : "max-h-0"
        }`}
      >
        <p className="text-base-content/70 font-grotesk leading-relaxed">
          {answer}
        </p>
      </div>
    </div>
  );
}

// ─── Log Type Icon ───────────────────────────────────────────────────────────

/** Log-menu glyphs from the Apple app (24px, 2px round stroke). */
function LogTypeIcon({ icon, stroke }: { icon: string; stroke: string }) {
  const common = {
    fill: "none",
    stroke,
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };

  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
      {icon === "dumbbell" && (
        <path {...common} d="M6 7V17M18 7V17M3 9V15M21 9V15M6 12H18" />
      )}
      {(icon === "heart" || icon === "cycle" || icon === "health") && (
        <path {...common} d="M19 14C20.49 12.54 22 10.79 22 8.5C22 7.04131 21.4205 5.64236 20.3891 4.61091C19.3576 3.57946 17.9587 3 16.5 3C14.74 3 13.5 3.5 12 5C10.5 3.5 9.26 3 7.5 3C6.04131 3 4.64236 3.57946 3.61091 4.61091C2.57946 5.64236 2 7.04131 2 8.5C2 10.8 3.5 12.55 5 14L12 21L19 14Z" />
      )}
      {icon === "leaf" && (
        <>
          <path {...common} d="M11 20C9.24406 20.0053 7.55025 19.3505 6.25452 18.1654C4.95878 16.9803 4.15577 15.3515 4.00474 13.6021C3.8537 11.8527 4.36569 10.1104 5.43915 8.72074C6.51261 7.33112 8.06913 6.3957 9.8 6.1C15.5 5 17 4.48 19 2C20 4 21 6.18 21 10C21 15.5 16.22 20 11 20Z" />
          <path {...common} d="M2 21C2 18 3.85 15.64 7.08 15" />
        </>
      )}
      {icon === "alert" && (
        <>
          <path {...common} d="M21.73 18L13.73 4C13.5556 3.6922 13.3026 3.43619 12.9969 3.25807C12.6912 3.07995 12.3438 2.9861 11.99 2.9861C11.6362 2.9861 11.2888 3.07995 10.9831 3.25807C10.6774 3.43619 10.4244 3.6922 10.25 4L2.25 18C2.07368 18.3054 1.98123 18.6519 1.98201 19.0045C1.98279 19.3571 2.07678 19.7032 2.25445 20.0078C2.43211 20.3124 2.68715 20.5646 2.9937 20.7388C3.30024 20.9131 3.64741 21.0032 4 21H20C20.3509 20.9996 20.6955 20.907 20.9993 20.7313C21.303 20.5556 21.5552 20.3031 21.7305 19.9991C21.9058 19.6951 21.998 19.3504 21.9979 18.9995C21.9979 18.6486 21.9054 18.3039 21.73 18Z" />
          <path {...common} d="M12 9V13M12 17H12.01" />
        </>
      )}
      {icon === "trophy" && (
        <path {...common} d="M6 9H4.5C3.83696 9 3.20107 8.73661 2.73223 8.26777C2.26339 7.79893 2 7.16304 2 6.5C2 5.83696 2.26339 5.20107 2.73223 4.73223C3.20107 4.26339 3.83696 4 4.5 4H6M6 9V2H18V9M6 9C6 10.5913 6.63214 12.1174 7.75736 13.2426C8.88258 14.3679 10.4087 15 12 15C13.5913 15 15.1174 14.3679 16.2426 13.2426C17.3679 12.1174 18 10.5913 18 9M18 9H19.5C20.163 9 20.7989 8.73661 21.2678 8.26777C21.7366 7.79893 22 7.16304 22 6.5C22 5.83696 21.7366 5.20107 21.2678 4.73223C20.7989 4.26339 20.163 4 19.5 4H18M4 22H20M10 14.66V17C10 17.55 9.53 17.98 9.03 18.21C7.85 18.75 7 20.24 7 22M14 14.66V17C14 17.55 14.47 17.98 14.97 18.21C16.15 18.75 17 20.24 17 22" />
      )}
      {icon === "file" && (
        <>
          <path {...common} d="M15 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V7L15 2Z" />
          <path {...common} d="M14 2V7H19M9 13H15M9 17H15" />
        </>
      )}
      {icon === "activity" && (
        <>
          <path {...common} d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11z" />
          <path {...common} d="M12 11.5a1.75 1.75 0 1 0 0-3.5 1.75 1.75 0 0 0 0 3.5z" />
        </>
      )}
    </svg>
  );
}

// ─── Main Landing Page ───────────────────────────────────────────────────────

function LandingPage() {
  const navigate = useNavigate();
  const [intercomLoaded, setIntercomLoaded] = useState(false);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = "auto";
    };
  }, []);

  useEffect(() => {
    console.log("Landing page mounted - initializing Intercom...");

    const initIntercom = () => {
      try {
        const anonymousId =
          safeGetItem("intercom_anonymous_landing_id") ||
          `landing_${Date.now()}_${Math.random()
            .toString(36)
            .substring(2, 11)}`;
        safeSetItem("intercom_anonymous_landing_id", anonymousId);

        const intercomConfig = {
          app_id: "wiedyikd",
          hide_default_launcher: false,
          user_id: anonymousId,
          name: "Landing Page Visitor",
          custom_attributes: {
            user_type: "landing_page_visitor",
            page: "landing_page",
          },
        };

        try {
          Intercom(intercomConfig);
          setIntercomLoaded(true);
          console.log("Intercom initialized successfully on landing page");
        } catch (intercomError: any) {
          if (
            intercomError?.name === "SecurityError" ||
            intercomError?.message?.includes("localStorage") ||
            intercomError?.message?.includes("Access is denied")
          ) {
            console.warn(
              "Intercom initialization skipped due to localStorage access restrictions:",
              intercomError,
            );
          } else {
            throw intercomError;
          }
        }
      } catch (error) {
        console.error("Error initializing Intercom:", error);
      }
    };

    initIntercom();

    return () => {
      console.log("Leaving landing page - hiding Intercom...");
      if (window.Intercom) {
        try {
          window.Intercom("hide");
          window.Intercom("update", {
            hide_default_launcher: true,
          });
        } catch (error) {
          console.error("Error hiding Intercom:", error);
        }
      }
    };
  }, []);

  const handleRegister = () => {
    navigate("/onboarding");
  };

  const openChat = () => {
    if (window.Intercom) {
      window.Intercom("show");
    } else {
      console.warn("Intercom not loaded yet");
    }
  };

  return (
    <div data-theme="peakx-light">
      <FloatingHeader></FloatingHeader>
      {/* Mobile Hero Section */}
      <section
        id="home"
        className="h-screen md:hidden hero-mobile w-screen flex items-start justify-center pt-32"
      >
        <div className="max-w-sm text-center space-y-2 px-4">
          <div className="flex items-center gap-2 justify-center">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <StarIcon key={i} className="w-4 h-4 text-warning" />
              ))}
            </div>
            <span className="text-sm text-base-content/60 font-grotesk">
              Trusted by athletes worldwide
            </span>
          </div>

          <h1 className="text-4xl font-bold font-heading leading-tight">
            The training intelligence app for athletes.
          </h1>

          {windowHeight >= 750 && (
            <p className="text-base text-base-content/70 font-grotesk leading-relaxed">
              Peak combines modern sports science and old school wisdom to help
              you{" "}
              <span className="bg-primary/20 text-base-content px-1.5 py-0.5 rounded font-semibold">
                reduce injury
              </span>{" "}
              and{" "}
              <span className="bg-primary/20 text-base-content px-1.5 py-0.5 rounded font-semibold">
                optimise performance
              </span>
              .
            </p>
          )}

          <div className="flex gap-3 justify-center pt-4">
            <button
              onClick={handleRegister}
              className="px-8 py-3 text-md text-center font-medium bg-primary text-primary-content rounded-3xl shadow-lg transition-opacity duration-200 font-grotesk hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              Get Started
            </button>
            <a
              href="#process"
              className="px-4 py-3 bg-white text-base font-medium border border-base-300 text-base-content rounded-full transition-colors duration-200 font-grotesk hover:bg-base-200 text-center"
            >
              See How It Works
            </a>
          </div>
        </div>
      </section>

      {/* Desktop Hero Section */}
      <section
        id="home-desktop"
        className="hidden md:flex h-screen hero-desktop items-center"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-left space-y-2 w-1/2">
            <div className="flex items-center gap-2 justify-center md:justify-start">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <StarIcon key={i} className="w-4 h-4 text-warning" />
                ))}
              </div>
              <span className="text-sm text-base-content/60 font-grotesk">
                Trusted by athletes worldwide
              </span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold font-heading leading-tight">
              The training intelligence app for athletes.
            </h1>

            <p className="text-lg md:text-xl text-base-content/70 font-grotesk leading-relaxed max-w-lg mx-auto md:mx-0 pt-4 pb-4">
              Peak combines modern sports science and old school wisdom to help
              you{" "}
              <span className="bg-primary/20 text-base-content px-1.5 py-0.5 rounded font-semibold">
                reduce injury
              </span>{" "}
              and{" "}
              <span className="bg-primary/20 text-base-content px-1.5 py-0.5 rounded font-semibold">
                optimise performance
              </span>
              .
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
              {windowHeight >= 670 && (
                <div className="flex justify-center">
                  <button
                    onClick={handleRegister}
                    className="px-10 py-3 text-md text-center font-medium bg-primary text-primary-content rounded-3xl shadow-lg transition-opacity duration-200 font-grotesk hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  >
                    Get Started
                  </button>
                </div>
              )}
              <a
                href="#process"
                className="px-8 py-3.5 text-base font-medium border border-base-300 text-base-content rounded-full transition-colors duration-200 font-grotesk hover:bg-base-200 text-center"
              >
                See How It Works
              </a>
            </div>
          </div>
          <div className="w-1/2"></div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="process">
        <Features />
      </section>

      {/* Wearables */}
      <section id="wearables">
        <Logos />
      </section>

      {/* Peak Readiness Score */}
      <section id="science">
        <PeakScore />
      </section>

      {/* What You Can Log */}
      <section id="log-types" className="bg-blue-50 py-24 px-6">
        <div className="max-w-6xl mx-auto flex flex-col gap-12">
          <div className="flex max-w-lg flex-col gap-4">
            <h2 className="text-3xl md:text-5xl font-bold font-grotesk">
              What you can log
            </h2>
            <p className="text-xl text-base-content/70 font-grotesk">
              Everything you need to monitor and understand your body in one
              place. Log daily in under <strong>60 seconds</strong>.
            </p>
          </div>

          {/* Log type cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {logTypes.map((log) => (
              <div
                key={log.name}
                className="bg-white rounded-xl p-5 flex items-start gap-4 hover:shadow-md transition-shadow duration-200"
              >
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
                  style={{ backgroundColor: log.chip }}
                >
                  <LogTypeIcon icon={log.icon} stroke={log.stroke} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <h3 className="text-base font-semibold font-grotesk">
                    {log.name}
                  </h3>
                  <p className="text-sm text-base-content/60 font-grotesk leading-relaxed">
                    {log.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center">
            <button
              onClick={handleRegister}
              className="px-8 py-3.5 text-base font-medium bg-primary text-primary-content rounded-full transition-opacity duration-200 font-grotesk hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              Get Started
            </button>
          </div>
        </div>
      </section>

      {/* Features Section (from V2) */}
      <section id="features" className="bg-base-100 py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold font-grotesk mb-4">
              What Peak does with the data
            </h2>
            <p className="text-lg text-base-content/70 font-grotesk max-w-2xl mx-auto">
              Powerful tools designed to help you make better training decisions
              every day.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {v2Features.slice(0, 2).map((feature, i) => (
              <div key={i} className="bg-base-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold font-grotesk mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-base-content/70 font-grotesk leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {v2Features.slice(2).map((feature, i) => (
              <div key={i} className="bg-base-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold font-grotesk mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-base-content/70 font-grotesk leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coaches */}
      <section id="coaches">
        <Users />
      </section>

      <section id="network">
        <Network />
      </section>

      {/* Testimonials Section — hidden for now */}
      {/* <section className="bg-base-100 py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold font-grotesk mb-4">
              What Athletes Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, i) => (
              <div
                key={i}
                className="bg-base-200 rounded-xl p-6 flex flex-col gap-4"
              >
                <div className="flex" aria-label={`${testimonial.rating} out of 5 stars`}>
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <StarIcon key={j} className="w-4 h-4 text-warning" />
                  ))}
                </div>
                <p className="text-base-content/80 font-grotesk leading-relaxed flex-1">
                  &ldquo;{testimonial.quote}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-base-300 flex items-center justify-center">
                    <span className="text-sm font-bold font-grotesk text-base-content/60">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold font-grotesk">
                      {testimonial.name}
                    </p>
                    <p className="text-xs text-base-content/60 font-grotesk">
                      {testimonial.role}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* FAQ Section — hidden for now */}
      {/* <section className="bg-base-200 py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold font-grotesk mb-4">
              FAQ
            </h2>
          </div>

          <div>
            {faqItems.map((item, i) => (
              <FAQItem key={i} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>
      </section> */}

      {/* Final CTA */}
      <section className="bg-base-100 py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col items-center text-center p-10 md:p-16 bg-base-300 rounded-2xl">
            <h2 className="text-3xl md:text-5xl font-bold font-grotesk mb-4">
              Ready to train smarter?
            </h2>
            <p className="text-lg text-base-content/70 font-grotesk mb-8 max-w-xl mx-auto">
              Join athletes worldwide who are using Peak to make better training
              decisions every day.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleRegister}
                className="px-10 py-3.5 text-base font-medium bg-primary text-primary-content rounded-full transition-opacity duration-200 font-grotesk hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                Get Started
              </button>
              <button
                onClick={openChat}
                disabled={!intercomLoaded}
                className={`px-10 py-3.5 text-base font-medium rounded-full transition-colors duration-200 font-grotesk border focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
                  intercomLoaded
                    ? "bg-base-100 text-base-content border-base-300 hover:bg-base-200"
                    : "bg-base-300 text-base-content/60 border-base-300 cursor-not-allowed"
                }`}
              >
                {intercomLoaded ? "Chat with Us" : "Loading chat..."}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-base-100 border-t border-base-300">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex flex-col items-center md:items-start gap-4">
              <div className="text-3xl font-groteskLogo font-bold">
                <h1 className="text-3xl font-groteskLogo">
                  PEAK
                  <span className="font-bold text-primary -ml-1">^</span>
                </h1>
              </div>
              <p className="text-base-content/70 text-sm">
                &copy; 2026 PEAK APP PTY LTD. All rights reserved.
              </p>
            </div>

            <div className="flex gap-6 text-sm">
              <a
                href="https://help.peak.app"
                target="_blank"
                rel="noopener noreferrer"
                className="text-base-content/70 hover:text-base-content transition-colors"
              >
                Help & Support
              </a>
              <a
                href="/privacy-policy"
                className="text-base-content/70 hover:text-base-content transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="/terms-of-service"
                className="text-base-content/70 hover:text-base-content transition-colors"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
